import { makeStyles, Text, tokens } from '@fluentui/react-components';
import {
  CallInbound16Regular,
  CallOutbound16Regular,
  CheckmarkCircle16Filled,
  Circle16Regular,
} from '@fluentui/react-icons';
import type { MultiTableItem, MultiTableViewDefinition, TableRecord } from '@powerportalspro/core';
import { useT } from '@powerportalspro/react';
import { Timeline } from '@powerportalspro/react-fluent';
import { useMemo, type ReactNode } from 'react';

// The page's account's phone calls and tasks, each drawn by a template of its own, beside its notes
// and messages, which keep the timeline's own drawing. The view names the columns the templates
// show, so the timeline reads them with each row.

const STRINGS_BASE = 'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Timelines.TimelineTemplatesDemo';

const COMPLETED = 1;

const useStyles = makeStyles({
  row: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXXS,
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalSNudge,
  },
});

// The raw value behind a column.
function read(record: TableRecord | null | undefined, column: string): unknown {
  const cell = record?.properties?.[column];
  return cell && typeof cell === 'object' && 'value' in cell ? cell.value : undefined;
}

export function TimelineTemplatesSample() {
  const styles = useStyles();
  const t = useT();

  const views = useMemo<MultiTableViewDefinition[]>(
    () => [
      {
        id: '8e1c4a7b-2d5f-4b9e-a3c6-7f0d2b4e6a18',
        displayName: t(`${STRINGS_BASE}.view-name`),
        tables: [
          { table: 'annotation' },
          { table: 'ppp_portalcommunication' },
          {
            table: 'phonecall',
            fetchXml: '<fetch><entity name="phonecall"><attribute name="directioncode" /><attribute name="phonenumber" /></entity></fetch>',
          },
          {
            table: 'task',
            fetchXml: '<fetch><entity name="task"><attribute name="statecode" /></entity></fetch>',
          },
        ],
      },
    ],
    [t],
  );

  const row = (icon: ReactNode, item: MultiTableItem, detail: string) => (
    <div className={styles.row}>
      <div className={styles.title}>
        {icon}
        <Text weight="semibold">{item.title}</Text>
      </div>
      <Text size={200}>{detail}</Text>
    </div>
  );

  return (
    <Timeline
      customViewDefinitions={views}
      pageSize={6}
      maxHeight="560px"
      templates={{
        // A phone call: which way it went, its number and when.
        phonecall: (item) => {
          const outgoing = read(item.record, 'directioncode') === true;
          const number = read(item.record, 'phonenumber');
          return row(
            outgoing ? <CallOutbound16Regular /> : <CallInbound16Regular />,
            item,
            t(`${STRINGS_BASE}.${outgoing ? 'call-to' : 'call-from'}`, [
              typeof number === 'string' ? number : '',
              item.startText ?? '',
            ]),
          );
        },
        // A task: whether it is done, and when it is due.
        task: (item) =>
          row(
            read(item.record, 'statecode') === COMPLETED ? <CheckmarkCircle16Filled /> : <Circle16Regular />,
            item,
            t(`${STRINGS_BASE}.due`, [item.startText ?? '']),
          ),
      }}
    />
  );
}
